import api from "../../Auth/Api";

// The adapter between the Client keys page and the backend's `clientkeys` admin group (Docs/ClientKeys.md §5). Every
// command string and every response shape the page relies on lives HERE: a change of the backend's answers is a fix in
// this one file. The normalizers never throw - a missing or odd answer becomes an empty row / table.
const call = (description, parts, options) => api.group("clientkeys")(description, parts, options);

export const PLATFORMS = [{ value: "android", label: "Android" }, { value: "ios", label: "iOS" }];
export const ANDROID_QUOTA = 1000000;  // Play Integrity: decodes / app / UTC day, raised 2026-11-04 from the 10,000 default (§4)
export const ANDROID_ALERT = 700000;   // the Health alert threshold (§4)

// A value the backend masked for a viewer ("••••1234")
export const isMasked = (v) => typeof v === "string" && v.startsWith("••••");
// The local mask of a full value (editors receive the full value; the page shows it masked until revealed)
export const mask = (v) => {
    const s = v == null ? "" : String(v);
    return isMasked(s) ? s : "••••" + s.slice(-4);
};

// Tokens travel as space-separated command parts: a token never contains whitespace (provider / key names and SDK key
// values never do). The page validates with this before sending.
export const isToken = (s) => typeof s === "string" && s.length > 0 && !/\s/.test(s);

const str = (v) => (v == null ? "" : String(v));
const num = (v) => (typeof v === "number" && isFinite(v) ? v : Number(v) || 0);

// `get` → { platform, keysVersion, providers: { provider: { key: value } }, updatedDate, updatedBy }
export function normalizeRow(r, platform) {
    const providers = {};
    const src = r && typeof r === "object" && r.providers && typeof r.providers === "object" ? r.providers : {};
    for (const [p, keys] of Object.entries(src)) {
        providers[p] = {};
        if (keys && typeof keys === "object") for (const [k, v] of Object.entries(keys)) providers[p][k] = str(v);
    }
    return {
        platform: str(r?.platform) || platform,
        keysVersion: r?.keysVersion == null ? null : num(r.keysVersion),
        providers,
        updatedDate: r?.updatedDate ?? null,
        updatedBy: str(r?.updatedBy),
        exists: !!(r && typeof r === "object" && (r.providers || r.keysVersion != null)),
    };
}

// `stats` → { days: [ { date, platform, ok, denied: { reason: n }, error, requests } ] }
export function normalizeStats(r) {
    const days = Array.isArray(r?.days) ? r.days : [];
    return days.map(d => {
        const denied = {};
        if (d?.denied && typeof d.denied === "object") for (const [k, v] of Object.entries(d.denied)) denied[k] = num(v);
        return {
            date: str(d?.date).slice(0, 10),      // "YYYY-MM-DD" (UTC day); an ISO date-time is cut to its day
            platform: str(d?.platform),
            ok: num(d?.ok),
            denied,
            deniedTotal: Object.values(denied).reduce((a, b) => a + b, 0),
            error: num(d?.error),
            requests: num(d?.requests),
        };
    });
}

// `log` → { rows: [ { date, platform, result, reason, app, device, licensing, versionCode, appVersion, country, ip? } ] }
export function normalizeLog(r) {
    const rows = Array.isArray(r?.rows) ? r.rows : [];
    return rows.map(x => ({
        date: x?.date ?? null,
        platform: str(x?.platform),
        result: str(x?.result),
        reason: str(x?.reason),
        app: str(x?.app),
        device: Array.isArray(x?.device) ? x.device.join(", ") : str(x?.device),
        licensing: str(x?.licensing),
        versionCode: x?.versionCode == null ? "" : str(x.versionCode),
        appVersion: str(x?.appVersion),
        country: str(x?.country),
        ip: x?.ip == null ? null : str(x.ip),
    }));
}

// A mutation's answer is used as the new row when it looks like one; otherwise the page reloads it.
const rowOrNull = (r, platform) => (r && typeof r === "object" && r.providers ? normalizeRow(r, platform) : null);

export const clientKeys = {
    get: (platform) => call("Loading the client keys", ["get", platform], { silent: true }).then(r => normalizeRow(r, platform)),
    set: (platform, provider, key, value) =>
        call("Saving the key", ["set", platform, provider, key, value], { payload: { platform, provider, key, value }, successMessage: "Key saved" })
            .then(r => rowOrNull(r, platform)),
    unset: (platform, provider, key) =>
        call(key ? "Removing the key" : "Removing the provider", key ? ["unset", platform, provider, key] : ["unset", platform, provider],
            { payload: key ? { platform, provider, key } : { platform, provider }, successMessage: key ? "Key removed" : "Provider removed" })
            .then(r => rowOrNull(r, platform)),
    rotate: (platform) =>
        call("Rotating the keys version", ["rotate", platform], { payload: { platform }, successMessage: "Keys version rotated" })
            .then(r => rowOrNull(r, platform)),
    stats: (days = 7) => call("Loading the client keys stats", ["stats", days], { silent: true }).then(normalizeStats),
    log: (n = 50) => call("Loading the client keys log", ["log", n], { silent: true }).then(normalizeLog),
};

export default clientKeys;
